import { useState } from 'react';
import { Alert } from '@clay-run/glaze/alert';
import { Button } from '@clay-run/glaze/button';
import { Text } from '@clay-run/glaze/text';
import { useMetaAudiences, useMetaStatus, usePublishRequestById, useUpdateAdRequest } from '../api-client';
import { fromRequest, fromRequestCreatives, missingFields, type DraftCreative } from '../draft';
import type { AdRequestDetail } from '../types';
import AdDraftPanel from './AdDraftPanel';
import { publishedNotice } from '../publish-notice';

export function RequestEditor({
  request,
  imageUrls,
  onDone,
}: {
  request: AdRequestDetail;
  /** Each saved creative's images, keyed by creative id. */
  imageUrls: { feed: Map<string, string>; vertical: Map<string, string> };
  onDone: () => void;
}) {
  const [draft, setDraft] = useState(() => fromRequest(request));
  const [creatives, setCreatives] = useState<DraftCreative[]>(() => fromRequestCreatives(request.creatives, imageUrls));
  const [videoProgress, setVideoProgress] = useState<number | null>(null);
  const status = useMetaStatus();
  const audiences = useMetaAudiences(status.data?.account != null);
  const update = useUpdateAdRequest(request.id);
  const publish = usePublishRequestById();
  const missing = missingFields(draft, creatives);
  const pending = update.isPending || publish.isPending;

  const save = ({ resubmit, andPublish }: { resubmit: boolean; andPublish: boolean }) => {
    setVideoProgress(creatives.some(c => c.media?.video?.file) ? 0 : null);
    update.mutate(
      { draft, creatives, audiences: audiences.data ?? [], resubmit, onVideoProgress: setVideoProgress },
      {
        onSuccess: () => (andPublish ? publish.mutate(request.id, { onSuccess: onDone }) : onDone()),
        onSettled: () => setVideoProgress(null),
      },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <Text size="sm" variant="tertiary">
          {request.canResubmit
            ? 'Make the changes the reviewer asked for, then resubmit to put it back in the review queue.'
            : 'Changes are saved to the request. It stays in the review queue.'}
        </Text>
        <div className="ml-auto flex items-center gap-2">
          {missing.length > 0 && (
            <Text size="sm" variant="tertiary">
              Needs {missing.join(', ')}
            </Text>
          )}
          {videoProgress !== null && (
            <Text size="sm" variant="tertiary">
              Uploading video to Meta… {Math.round(videoProgress * 100)}%
            </Text>
          )}
          <Button variant="secondary" onClick={onDone} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant={request.canResubmit || request.canReview ? 'secondary' : 'primary'}
            onClick={() => save({ resubmit: false, andPublish: false })}
            disabled={missing.length > 0 || pending}
            loading={update.isPending && !update.variables?.resubmit}
          >
            Save changes
          </Button>
          {request.canResubmit && (
            <Button
              variant="primary"
              onClick={() => save({ resubmit: true, andPublish: false })}
              disabled={missing.length > 0 || pending}
              loading={update.isPending && update.variables?.resubmit}
            >
              Save and resubmit
            </Button>
          )}
          {request.canReview && (
            <Button
              variant="primary"
              onClick={() => save({ resubmit: false, andPublish: true })}
              disabled={missing.length > 0 || pending}
              loading={publish.isPending}
            >
              Save and publish paused
            </Button>
          )}
        </div>
      </div>
      {update.error && (
        <Alert status="error" error={update.error}>
          {update.error.message}
        </Alert>
      )}
      {publish.error && <Alert status="warning">{publishedNotice(publish.error)}</Alert>}
      <div className="flex h-[calc(100vh-280px)] min-h-[600px] flex-col">
        <AdDraftPanel
          metaDraft={draft}
          onMetaDraftChange={setDraft}
          creatives={creatives}
          onCreativesChange={setCreatives}
          metaConnected={status.data?.account != null}
        />
      </div>
    </div>
  );
}
